import { last } from 'es-toolkit';
import { SourceTypes } from '../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../utils';
import type { DataValue, PictureValueType, ProviderType } from '../types';
import type { VimeoResponseType, VimeoSourceValue } from './types';
import {
  vimeoChannelURLRegExp,
  vimeoGroupURLRegExp,
  vimeoShowcaseURLRegExp,
  vimeoSpecificVideoURLRegExp,
  vimeoUserURLRegExp
} from './vimeoURLPattern';

const PLATFORM = SourceTypes.VIMEO;

const select = (
  response: VimeoResponseType
): DataValue<PictureValueType[]>[] => {
  handleResponseError(response, PLATFORM);

  const dataArray = response.data ?? [response];
  const currentData = dataArray.filter((item) => {
    const { embed = 'public', view = 'anybody' } = item.privacy ?? {};

    return (
      ['public', 'whitelist'].includes(embed) &&
      ['anybody', 'disable'].includes(view)
    );
  });

  return currentData.map(
    ({
      user,
      description,
      pictures,
      link,
      metadata,
      player_embed_url: playerEmbedUrl,
      resource_key: resourceKey,
      release_time: releaseTime
    }) => {
      const text = formatText(description, PLATFORM);
      const formattedImages = pictures.sizes.map(({ width, height, link }) => ({
        width,
        height,
        url: link
      }));

      return {
        text,
        id: resourceKey,
        timestamp: Date.parse(releaseTime),
        source: {
          type: PLATFORM,
          url: link
        },
        user: {
          name: user.name,
          image: last(user.pictures.sizes)?.link,
          url: user.link
        },
        media: playerEmbedUrl
          ? {
              type: 'video',
              src: playerEmbedUrl,
              thumbnail: formattedImages
            }
          : undefined,
        counters: {
          comments: metadata.connections.comments.total ?? 0,
          likes: metadata.connections.likes.total ?? 0
        }
      };
    }
  );
};

export function createVimeoDataProvider(
  sourceValue: VimeoSourceValue,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<VimeoResponseType> {
  const { source = '', connectionType, authentication: auth } = sourceValue;
  const identity = auth?.identity ?? '';

  const followButtonLink =
    connectionType === 'vimeoChannel' ? source : undefined;

  const key = [...globalKey, ...filtersKey, source];

  const page = (rawData: VimeoResponseType | undefined) => {
    if (rawData) {
      if (!rawData.paging?.next) {
        return;
      }

      return buildProxyURL({
        apiSource: PLATFORM,
        params: {
          q: rawData.paging.next,
          user_id: identity
        }
      });
    }

    const requestURL = (() => {
      if (connectionType === 'vimeoChannel') {
        const [_all, channel] = source.match(vimeoChannelURLRegExp) ?? [];

        return `channels/${channel}/videos`;
      }

      if (connectionType === 'vimeoGroup') {
        const [_all, group] = source.match(vimeoGroupURLRegExp) ?? [];

        return `groups/${group}/videos`;
      }

      if (connectionType === 'vimeoShowcase') {
        const [_all, showcase] = source.match(vimeoShowcaseURLRegExp) ?? [];

        return `albums/${showcase}/videos`;
      }

      if (connectionType === 'vimeoSpecificVideo') {
        const [_all, video] = source.match(vimeoSpecificVideoURLRegExp) ?? [];

        return `videos/${video}`;
      }

      if (connectionType === 'vimeoUser') {
        const [_all, user] = source.match(vimeoUserURLRegExp) ?? [];

        return `users/${user}/videos`;
      }

      return '';
    })();

    return buildProxyURL({
      apiSource: PLATFORM,
      params: {
        q: requestURL,
        user_id: identity,
        sort: 'default'
      }
    });
  };

  return {
    key,
    page,
    select,
    link: followButtonLink
  };
}
